import { App } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Geolocation, type Position } from '@capacitor/geolocation';
import { useEffect, useState } from 'react';

export interface Coords {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export enum GeolocationStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

/** Why there is no location, when it is something the user can fix in Settings. */
export type GeolocationIssue = 'denied' | 'off';

interface GeolocationStatePending {
  status: GeolocationStatus.PENDING;
}

interface GeolocationStateSuccess {
  status: GeolocationStatus.SUCCESS;
  coords: Coords;
}

interface GeolocationStateError {
  status: GeolocationStatus.ERROR;
  reason?: GeolocationIssue;
}

export type GeolocationState =
  | GeolocationStatePending
  | GeolocationStateSuccess
  | GeolocationStateError;

// @capacitor/geolocation error codes; `1` is the browser's PERMISSION_DENIED.
const DENIED_CODES: unknown[] = ['OS-PLUG-GLOC-0003', 'OS-PLUG-GLOC-0008', 1];
const OFF_CODES: unknown[] = ['OS-PLUG-GLOC-0007', 'OS-PLUG-GLOC-0009', 'OS-PLUG-GLOC-0017'];

const issueOf = (err: unknown): GeolocationIssue | undefined => {
  const code = (err as { code?: unknown } | null)?.code;
  if (DENIED_CODES.includes(code)) return 'denied';
  if (OFF_CODES.includes(code)) return 'off';
  return undefined;
};

const WAIT_MS = 10_000;
/** Accuracy a log entry waits for; screens that only need "roughly here" take the first fix. */
export const GOOD_ENOUGH_M = 30;
// iOS ignores `maximumAge` and can hand back a cached fix from wherever the phone last was.
const FRESH_MS = 30_000;
// A desktop browser hands a computer that hasn't moved its last fix again, old timestamp and all,
// and sends nothing newer until it moves; refusing it left every entry after the first empty.
const IS_WEB = Capacitor.getPlatform() === 'web';

/**
 * One fresh GPS fix, for a caller that needs a position at a moment of its own rather than at
 * mount — e.g. each navigation-log entry. Rejects when there is none; `issueOf` says why.
 */
// ponytail: waits up to WAIT_MS for a fresh fix within `goodEnoughM`, else settles for the best
// fresh one seen — below deck that can be ~100 m. A longer wait is the knob if logs need better.
// On the web an old fix is the last resort, taken only once the wait runs out with nothing fresh,
// so a desktop entry can take the full WAIT_MS to fill; a shorter web wait is the knob for that.
export const bestFix = (goodEnoughM = GOOD_ENOUGH_M) =>
  new Promise<Position>((resolve, reject) => {
    let best: Position | undefined;
    let stale: Position | undefined;
    let watchId: string | undefined;
    let done = false;
    const timer = setTimeout(() => finish(), WAIT_MS);

    const finish = (err?: unknown) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (watchId) void Geolocation.clearWatch({ id: watchId });
      const fix = best ?? stale;
      if (fix) resolve(fix);
      else reject(err ?? new Error('No fresh location fix in time'));
    };

    Geolocation.watchPosition(
      // Android spaces watch updates by `timeout` unless told otherwise: one fix per wait.
      { enableHighAccuracy: true, timeout: WAIT_MS, interval: 1000, minimumUpdateInterval: 500 },
      (pos, err) => {
        if (!pos) {
          // "No fix yet" and timeouts recover on their own; give up early only on what the user must fix.
          if (issueOf(err)) finish(err);
          return;
        }
        if (Date.now() - pos.timestamp > FRESH_MS) {
          if (IS_WEB) stale = pos;
          return;
        }
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        if (pos.coords.accuracy <= goodEnoughM) finish();
      },
    ).then((id) => {
      watchId = id;
      if (done) void Geolocation.clearWatch({ id });
    }, finish);
  });

/**
 * `goodEnoughM` defaults to the first fresh fix, as fast as the old one-shot read. With
 * `enabled` false nothing is asked for and the state stays PENDING: no prompt a screen has no use for.
 */
export const useGeolocation = (goodEnoughM = Infinity, enabled = true) => {
  const [state, setState] = useState<GeolocationState>({ status: GeolocationStatus.PENDING });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let located = false;
    let running = false;

    // No requestPermissions first: watchPosition prompts by itself, and on Android it also offers
    // the system "turn on location" dialog where requestPermissions would just fail.
    const locate = async () => {
      if (located || running) return;
      running = true;
      try {
        const { coords } = await bestFix(goodEnoughM);
        located = true;
        if (cancelled) return;
        const { latitude, longitude, accuracy } = coords;
        setState({ status: GeolocationStatus.SUCCESS, coords: { latitude, longitude, accuracy } });
      } catch (err) {
        if (!cancelled) setState({ status: GeolocationStatus.ERROR, reason: issueOf(err) });
      } finally {
        running = false;
      }
    };

    void locate();
    // Back from Settings with access granted: try again so the location fills in by itself.
    const sub = App.addListener('resume', () => void locate());
    return () => {
      cancelled = true;
      void sub.then((s) => s.remove());
    };
  }, [goodEnoughM, enabled]);

  return state;
};

const AppSettings = registerPlugin<{
  open(options: { target: 'app' | 'location' }): Promise<void>;
}>('AppSettings');

/** Opens the OS settings page where the user can fix `issue`. Native only. */
export const openLocationSettings = (issue: GeolocationIssue) => {
  // iOS has no native call for this: Capacitor hands any non-app URL to UIApplication.open, and
  // `app-settings:` is the app's own page, which carries the Location row.
  if (Capacitor.getPlatform() === 'ios') window.location.href = 'app-settings:';
  else void AppSettings.open({ target: issue === 'off' ? 'location' : 'app' });
};
